import { type FC, useEffect, useRef } from "react";

import { Alert } from "@mantine/core";
import { FormattedMessage } from "react-intl";

import { type AssistantChatMessageModel } from "../model";

import { AssistantChatMessageItem, AssistantChatThinkingItem } from "./AssistantChatMessageItem";
import styles from "./AssistantChatMessageList.module.css";
import { AssistantChatSuggestions } from "./AssistantChatSuggestions";
import { messages } from "./messages.generated";

interface Props {
    messages: AssistantChatMessageModel[];
    isSending: boolean;
    hasError: boolean;
    onSuggestionSelect: (prompt: string) => void;
}

export const AssistantChatMessageList: FC<Props> = ({ messages: items, isSending, hasError, onSuggestionSelect }) => {
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [items.length, isSending, hasError]);

    // No messages means the empty state, even while a stale request settles after Start over.
    if (items.length === 0) {
        return (
            <div className={styles.emptyState}>
                <AssistantChatSuggestions onSelect={onSuggestionSelect} disabled={isSending} />
            </div>
        );
    }

    return (
        <div className={styles.list} aria-live="polite">
            {items.map((message) => (
                <AssistantChatMessageItem key={message.id} message={message} />
            ))}
            {isSending && <AssistantChatThinkingItem />}
            {hasError && (
                <Alert className={styles.errorAlert} color="pink" variant="light" radius="md" role="alert">
                    <FormattedMessage {...messages.errorFallback} />
                </Alert>
            )}
            <div ref={endRef} />
        </div>
    );
};
